<template>
  <fragment>
    <div class="home job-profile-details font_Montserrat v3_color_black_knight px-3 mt-0">
      <v-wait :for="loaderId">
        <template slot="waiting">
          <HalfCircleSpinner />
          <div class="spinner-message my-3 text-center">
            Loading the profile data...
          </div>
        </template>
        <b-row v-if="profile" :class="{'mt-4': isMobile}">
          <b-col cols="12" class="px-4 px-md-3">
            <job-profile-title-card
              class="mb-3 px-4 px-md-0 mx-3 mx-md-0"
              :job-profile="profile"
              :is-job-profile-new="isJobProfileNew"
              :post-to-exchange-loading="postToExchangeLoading"
              :post-to-exchange-disabled="!isAllJobQuestionsAnswered"
              :is-job-question-ids-dirty="isJobQuestionIdsDirty"
              :is-job-posted-to-exchange="isJobPostedToExchange"
              @rename="$refs.renameModal.show()"
              @delete="onRemoveJobProfile"
              @cancel="$router.back()"
              @copy="onCopyMenuClick"
              @post-to-exchange="onShowPostToExchangeModal"
              @cancel-new-job-profile="onCancelNewJobProfile"
            />
          </b-col>
        </b-row>
        <div class="card mt-2 v3_box_teal">
          <div class="card-body v3_box_teal_body">
            <div class="box-body-wrapper flex-column p-3">
              <b-row v-if="profile"  class="w-100">
                <b-col sm="12" md="4" lg="3" class="job-profile-details__side-bar">
                  <div class="border_1 border_light_teal h-100 w-100 p-3">
                    <job-profile-preferences
                      class="px-4 px-md-0"
                      :job-questions="requiredQuestions"
                      :optional-questions="optionalQuestions"
                      :selected-optional-questions="selectedOptionalQuestions"
                      :edited-question-id="editedJobQuestionId"
                      @add-preference="$refs.addPreferenceModal.show()"
                      @unselect-question="onOptionalUnselect"
                      @select-question="onSelectQuestion"
                    />
                  </div>
                </b-col>
                <b-col sm="12" md="4" lg="6">
                  <div class="px-4 px-md-0 border_1 border_light_teal h-100 w-100 p-3">
                    <preference-details
                      ref="preferenceDetails"
                      class="mb-3"
                      :job-question="editedQuestion"
                      :is-save-button-enabled="isJobQuestionAnswered"
                      :is-save-loading="isSavingInProcess"
                      @update-job-question="onUpdateJobQuestionPreference"
                      @save="onProfileSave"
                    />
                  </div>
                </b-col>
                <b-col sm="12" md="4" lg="3" class="job-profile-details__side-bar">
                  <div class="px-4 px-md-0 border_1 border_light_teal h-100 w-100 p-3">
                    <job-profile-preference-answer-list
                      class="px-4 px-md-0"
                      :job-profile-title="profile.title"
                      :job-questions="requiredAnsweredQuestions"
                      :optional-questions="optionalAnsweredQuestions"
                      :selected-optional-questions="selectedOptionalQuestions"
                      :edited-question-id="editedJobQuestionId"
                      @add-preference="$refs.addPreferenceModal.show()"
                      @unselect-question="onOptionalUnselect"
                      @select-question="onSelectQuestion"
                    />
                  </div>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>
      </v-wait>
    </div>
    <modal-re-init-modal-behaviour>
      <template slot-scope="{ keyId, keyUpdater }">
        <b-modal
          v-if="profile"
          hide-footer
          id="job-profile-modal"
          ref="renameModal"
          content-class="rounded-0"
          body-class="pb-4"
          size="lg"
          @show="keyUpdater"
        >
          <div slot="modal-title">
            <span class="card_title">Name Job Profile</span>
          </div>
          <job-profile-form
            :key="keyId"
            :initial-data="profile"
            @cancel="$refs.renameModal.hide()"
            @cancel-create="onCancelNewJobProfile"
            @form-submit="onRenameJobProfile"
          />
        </b-modal>
      </template>
    </modal-re-init-modal-behaviour>
    <modal-re-init-modal-behaviour>
      <template slot-scope="{ keyId, keyUpdater }">
        <b-modal
          v-if="profile"
          hide-footer
          hide-header
          size="md"
          ref="addPreferenceModal"
          body-class="pb-4"
          @show="keyUpdater"
        >
          <optional-preferences-selector
            :key="keyId"
            :optional-questions="optionalQuestions"
            :initial-selected-ids="selectedOptionalQuestionsIds"
            @change-selected="onChangeSelected"
            @change-canceled="onChangeCanceled"
          />
        </b-modal>
      </template>
    </modal-re-init-modal-behaviour>
    <b-modal
      id="job-profile-copy-modal"
      ref="copyModal"
      :hide-footer="true"
      content-class="rounded-0"
      body-class="pb-4"
    >
      <div slot="modal-title">
        <span class="card_title">Copy Job Profile</span>
      </div>
      <job-profile-copy-form
        @cancel="$refs.copyModal.hide()"
        @form-submit="$refs.copyModal.hide()"
        :job-profile-id="parseInt($route.params.id)"
      />
    </b-modal>

    <post-to-exchange-modal
      v-model="isPostToExchangeModalVisible"
      :title="profile ? profile.title : ''"
      @ok="postToExchange"
      @cancel="isPostToExchangeModalVisible = false"
    />
  </fragment>
</template>

<script>
import Api from '@/providers/Api';
import cloneDeep from 'lodash/cloneDeep';
import JobProfileForm from '@/components/exchange/JobProfileForm';
import PreferenceDetails from '@/components/exchange/job-profile-details/PreferenceDetails';
import JobProfilePreferences from '@/components/exchange/preference-list/JobProfilePreferences';
import JobProfileTitleCard from '@/components/exchange/job-profile-details/JobProfileTitleCard';
import OptionalPreferencesSelector from '@/components/exchange/preference-list/OptionalPreferencesSelector';
import { HalfCircleSpinner } from 'epic-spinners';
import { handleServerErrorWithValidation } from '@/helpers/ToastedHelper';
import Help from '@/providers/Help';
import {
  DELETE_JOB_PROFILE_ACTION,
  UPDATE_JOB_PROFILE_ACTION,
  UPDATE_JOB_PROFILE_MUTATION,
} from '@/store/jobProfiles';
import { JOB_QUESTION_TYPES } from '@/store';
import ModalReInitModalBehaviour from '@/behaviours/ForceUpdateBehaviour';
import {
  createGeographyQuestionObjectFromJobProfile,
  createJobQuestionsSorter,
  getTypeAndValueFromGeographicQuestion,
  isJobQuestionAnswered,
} from '@/helpers/jobQuestionsHelper';
import JobProfileCopyForm from '@/components/exchange/JobProfileCopyForm';
import PostToExchangeModal from '@/components/exchange/job-profile-details/PostToExchangeModal';
import JobProfilePreferenceAnswerList from '@/components/exchange/preference-list/JobProfilePreferenceAnswerList';
import MobileDetectionMixin from "@/mixins/MobileDetectionMixin";

const modifiedConfirmMessage =
  'The job profile has been modified but not saved yet. Would you like to continue and lose your changes?';

const jobQuestionsSorter = createJobQuestionsSorter('display_order');

export default {
  name: 'JobProfileDetails',
  mixins: [
    MobileDetectionMixin
  ],
  components: {
    JobProfileForm,
    JobProfileCopyForm,
    HalfCircleSpinner,
    JobProfilePreferences,
    PreferenceDetails,
    JobProfileTitleCard,
    OptionalPreferencesSelector,
    ModalReInitModalBehaviour,
    PostToExchangeModal,
    JobProfilePreferenceAnswerList,
  },
  data: function () {
    return {
      profile: null,
      allJobQuestions: [],
      answeredJobQuestions: [],
      isJobQuestionIdsDirty: false,
      hasJobQuestionIdsErrors: false,
      editedJobQuestionId: null,
      isSavingInProcess: false,
      isPostToExchangeModalVisible: false,
      hasJobProfileTheSavedPreferences: false,
      postToExchangeLoading: false,
      isPostToExchangeProcess: false,
      isEditedJobOffer: false,
      isJobPostedToExchange:false
    };
  },
  created: async function () {
    this.JOB_QUESTION_TYPES = JOB_QUESTION_TYPES;
    await this.getData();
    this.$nextTick(() => {
      if (this.profile.is_posted) {
        this.isJobPostedToExchange = true;
        // this.$toasted.error(
        //   "This job profile already posted and can't be edited",
        //   {
        //     duration: 3000,
        //   }
        // );
        // this.$router.push('/exchange');
      }
    });
  },
  beforeMount: function () {
    window.addEventListener('beforeunload', this.preventEventIfDirtyOrErrors);
    this.$once('hook:beforeDestroy', () => {
      window.removeEventListener(
        'beforeunload',
        this.preventEventIfDirtyOrErrors
      );
    });
  },
  computed: {
    loaderId: function () {
      return `loading_${this.$options.name}`;
    },
    jobQuestions: function () {
      return this.allJobQuestions
        .filter(
          (q) =>
            this.profile.jobQuestionIds.includes(q.id) ||
            q.type === JOB_QUESTION_TYPES.GEOGRAPHY
        )
        .sort(jobQuestionsSorter);
    },
    requiredQuestions: function () {
      return this.allJobQuestions
        .filter(
          (q) =>
            q.required &&
            (this.profile.jobQuestionIds.includes(q.id) ||
              q.type === JOB_QUESTION_TYPES.GEOGRAPHY)
        )
        .sort(jobQuestionsSorter);
    },
    optionalQuestions: function () {
      return this.allJobQuestions
        .filter(
          (q) =>
            (!q.required || !this.profile.jobQuestionIds.includes(q.id)) &&
            q.type !== JOB_QUESTION_TYPES.GEOGRAPHY
        )
        .sort(jobQuestionsSorter);
    },
    requiredAnsweredQuestions: function () {
      return this.answeredJobQuestions
        .filter(
          (q) =>
            q.required &&
            (this.profile.jobQuestionIds.includes(q.id) ||
              q.type === JOB_QUESTION_TYPES.GEOGRAPHY)
        )
        .sort(jobQuestionsSorter);
    },
    optionalAnsweredQuestions: function () {
      return this.answeredJobQuestions
        .filter(
          (q) =>
            (!q.required || !this.profile.jobQuestionIds.includes(q.id)) &&
            q.type !== JOB_QUESTION_TYPES.GEOGRAPHY
        )
        .sort(jobQuestionsSorter);
    },
    selectedOptionalQuestionsIds: function () {
      return this.optionalQuestions
        .filter(({ id }) => this.profile.jobQuestionIds.includes(id))
        .map(({ id }) => id);
    },
    selectedOptionalQuestions: function () {
      return this.optionalQuestions.filter(({ id }) =>
        this.profile.jobQuestionIds.includes(id)
      );
    },
    editedQuestion: function () {
      return this.allJobQuestions.find(
        ({ id }) => id === this.editedJobQuestionId
      );
    },
    isJobQuestionAnswered: function () {
      return this.isDirty() && isJobQuestionAnswered(this.editedQuestion);
    },
    isJobProfileNew: function () {
      return this.$route.params && this.$route.params.isNew;
    },
    isAllJobQuestionsAnswered: function () {
      return this.jobQuestions.every(
        (jq) => !jq.required || isJobQuestionAnswered(jq)
      );
    },
  },
  methods: {
    getData: async function () {
      try {
        this.$wait.start(this.loaderId);
        const [allJobQuestions, profile] = await Promise.all([
          Api.getJobQuestionsDataForProfile(this.$route.params.id),
          Api.getJobProfileData(this.$route.params.id),
        ]);
        allJobQuestions.forEach((question) => {
          question.jobQuestionVariables.forEach((jQv) => {
            if (
              !Array.isArray(jQv.jobProfileQuestionResponses) ||
              jQv.jobProfileQuestionResponses.length === 0
            ) {
              jQv.jobProfileQuestionResponses = [
                {
                  job_profile_id: this.$route.params.id,
                  job_question_variable_id: jQv.id,
                  value: null,
                  value_string: null,
                },
              ];
            }
          });
        });
        allJobQuestions.push(
          createGeographyQuestionObjectFromJobProfile(profile)
        );
        this.allJobQuestions = allJobQuestions;
        this.profile = profile;
        this.getAnsweredJobQuestions();
      } catch (error) {
        process.env.NODE_ENV !== 'production' && console.error(error);
        this.$toasted.error('Unable to upload profile data', {
          duration: 3000,
        });
      } finally {
        this.$wait.end(this.loaderId);
      }
    },
    onRenameJobProfile: async function (newTitle) {
      // eslint-disable-next-line no-unused-vars
      const { jobQuestions, ...restProfileData } = this.profile || {};
      this.$wait.start('menu-loading');
      await this.saveJobProfile({ ...restProfileData, title: newTitle });
      this.$wait.end('menu-loading');

      if (this.isPostToExchangeProcess) {
        this.isPostToExchangeProcess = false;
        this.isPostToExchangeModalVisible = true;
      }
    },
    onRemoveJobProfile: function () {
      Help.confirm.show('Delete this Job Profile?', 'Delete', () => {
        this.removeJobProfile();
      });
    },
    removeJobProfile: async function () {
      try {
        this.$wait.start('menu-loading');
        await this.$store.dispatch(DELETE_JOB_PROFILE_ACTION, this.profile.id);
        this.$router.push('/exchange');
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted);
      } finally {
        this.$wait.end('menu-loading');
      }
    },
    updateJobQuestionPreference: function (jobQuestionId, variableId, value) {
      const [jobQuestionIndex, variableIndex] = this.allJobQuestions.reduce(
        (acc, jQ, index) => {
          let [jobQuestionIndex, variableIndex] = acc;
          if (jQ.id === jobQuestionId) {
            jobQuestionIndex = index;
            const varIndex = jQ.jobQuestionVariables.findIndex(
              (jQv) => jQv.id === variableId
            );
            if (varIndex > -1) {
              variableIndex = varIndex;
            }
          }
          return [jobQuestionIndex, variableIndex];
        },
        [null, null]
      );
      if (jobQuestionIndex !== null && variableIndex !== null) {
        this.$set(
          this.allJobQuestions[jobQuestionIndex].jobQuestionVariables[
            variableIndex
          ].jobProfileQuestionResponses[0],
          'value',
          value
        );
      }
    },
    updateSelectOnePreference: function (jobQuestionId, variableId) {
      this.$nextTick(() => {
        const jobQuestionIndex = this.allJobQuestions.findIndex(({ id }) => {
          return id === jobQuestionId;
        });
        if (jobQuestionIndex === -1) return;
        this.allJobQuestions[jobQuestionIndex].jobQuestionVariables.forEach(
          (jQv, indx) => {
            this.$set(
              this.allJobQuestions[jobQuestionIndex].jobQuestionVariables[indx]
                .jobProfileQuestionResponses[0],
              'value',
              jQv.id === variableId ? 1 : 0
            );
          }
        );
      });
    },
    updateYesNoPreference: function (jobQuestionId, value) {
      const jobQuestionIndex = this.allJobQuestions.findIndex(
        ({ id }) => id === jobQuestionId
      );
      this.$set(
        this.allJobQuestions[jobQuestionIndex].jobQuestionVariables[0]
          .jobProfileQuestionResponses[0],
        'value',
        value
      );
    },
    updateGeographicPreference: function (jobQuestionId, value) {
      const jobQuestionIndex = this.allJobQuestions.findIndex(
        ({ id }) => id === jobQuestionId
      );
      this.$set(
        this.allJobQuestions[jobQuestionIndex].jobQuestionVariables[0]
          .jobProfileQuestionResponses,
        '0',
        value
      );
    },
    onUpdateJobQuestionPreference: function (type, jobQuestionId, ...rest) {
      switch (type) {
        case JOB_QUESTION_TYPES.SLIDER:
        case JOB_QUESTION_TYPES.RATING:
        case JOB_QUESTION_TYPES.SELECT_ANY:
        case JOB_QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE:
          this.updateJobQuestionPreference(jobQuestionId, ...rest);
          break;
        case JOB_QUESTION_TYPES.SELECT_ONE:
          this.updateSelectOnePreference(jobQuestionId, ...rest);
          break;
        case JOB_QUESTION_TYPES.YES_NO:
          this.updateYesNoPreference(jobQuestionId, ...rest);
          break;
        case JOB_QUESTION_TYPES.GEOGRAPHY:
          this.updateGeographicPreference(jobQuestionId, ...rest);
          break;
      }
      this.isJobQuestionIdsDirty = true;
      this.hasJobQuestionIdsErrors = this.hasErrors();
    },
    onChangeSelected: function (selectedOptionalIds) {
      let newJobQuestionsIds = this.profile.jobQuestionIds.filter(
        (id) =>
          !this.selectedOptionalQuestionsIds.includes(id) &&
          !selectedOptionalIds.includes(id)
      );
      newJobQuestionsIds = newJobQuestionsIds.concat(selectedOptionalIds);
      this.$set(this.profile, 'jobQuestionIds', newJobQuestionsIds);
      this.$refs.addPreferenceModal.hide();
      this.isJobQuestionIdsDirty = true;
      this.hasJobQuestionIdsErrors = this.hasErrors();
    },
    onChangeCanceled: function () {
      this.$refs.addPreferenceModal.hide();
    },
    onOptionalUnselect: function (id) {
      if (this.isSavingInProcess) return;

      const index = this.profile.jobQuestionIds.findIndex((qId) => qId === id);
      if (index < 0) return;
      this.profile.jobQuestionIds.splice(index, 1);
      this.isJobQuestionIdsDirty = true;
    },
    onSelectQuestion: function (id) {
      if (this.isSavingInProcess) return;

      if (this.hasErrors()) {
        return this.$toasted.error(
          'Some of your preference data are not valid, please check the form!',
          {
            duration: 3000,
          }
        );
      }
      this.editedJobQuestionId = id;
      // if (this.profile.is_posted) {
      //   this.isJobPostedToExchange = true;
      // }
    },
    onProfileSave: async function () {
      const geographicQuestion = this.jobQuestions.find(
        ({ type }) => type === JOB_QUESTION_TYPES.GEOGRAPHY
      );
      const { type, value } =
        getTypeAndValueFromGeographicQuestion(geographicQuestion);
      const data = {
        ...this.profile,
        jobQuestions: this.allJobQuestions.filter(
          ({ type }) => type !== JOB_QUESTION_TYPES.GEOGRAPHY
        ),
        geographic_preference_type: type,
        geographic_preference_value: value ? JSON.stringify(value) : null,
      };
      this.isSavingInProcess = true;
      try {
        await this.saveJobProfile(data);
        if (!this.isJobProfileNew) {
          this.isEditedJobOffer = true;
        }
        this.$toasted.success('Preference data saved', {
          position: 'top-center',
          duration: 3000,
        });
        this.resetValidation();
        this.isSavingInProcess = false;
        this.hasJobProfileTheSavedPreferences = true;
        this.getAnsweredJobQuestions();
      } finally {
        this.isSavingInProcess = false;
      }
    },
    saveJobProfile: async function (updatedProfile) {
      if (this.hasErrors()) {
        return this.$toasted.error(
          'Some of your preference data are not valid, please check the form!',
          {
            duration: 3000,
          }
        );
      }
      try {
        this.$refs.renameModal.hide();
        const profile = await this.$store.dispatch(
          UPDATE_JOB_PROFILE_ACTION,
          updatedProfile
        );
        this.profile = {
          ...this.profile,
          ...profile,
        };
        if (this.profile.is_posted) {
          this.isJobPostedToExchange = false;
        }
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted);
        throw error;
      }
    },
    onCopyMenuClick: function () {
      if (!this.isDirty() || window.confirm(modifiedConfirmMessage)) {
        this.$refs.copyModal.show();
      }
    },
    isDirty: function () {
      return (
        this.isJobQuestionIdsDirty ||
        (this.$refs.preferenceDetails &&
          (typeof this.$refs.preferenceDetails.isDirty === 'function'
            ? this.$refs.preferenceDetails.isDirty()
            : false))
      );
    },
    hasErrors: function () {
      return typeof this.$refs.preferenceDetails.hasErrors === 'function'
        ? this.$refs.preferenceDetails.hasErrors()
        : false;
    },
    resetValidation: function () {
      this.isJobQuestionIdsDirty = false;
      this.hasJobQuestionIdsErrors = false;
      return this.$refs.preferenceDetails.resetValidation();
    },
    preventEventIfDirtyOrErrors: function (event) {
      if (!this.isSavingInProcess && !this.isDirty() && !this.hasErrors())
        return;
      event.preventDefault();
      event.returnValue = '';
    },
    getIsPreferenceRequired: function (id) {
      return (
        Array.isArray(this.profile.requiredJobQuestionIds) &&
        this.profile.requiredJobQuestionIds.includes(id)
      );
    },
    updateRequiredValues: function (id, checked) {
      if (checked) {
        this.profile.requiredJobQuestionIds.push(id);
      } else {
        this.profile.requiredJobQuestionIds =
          this.profile.requiredJobQuestionIds.filter((jqId) => jqId !== id);
      }
    },
    onShowPostToExchangeModal: function () {
      if (!this.profile.title) {
        this.$refs.renameModal.show();
        this.isPostToExchangeProcess = true;
      } else {
        this.isPostToExchangeModalVisible = true;
      }
    },
    postToExchange: async function () {
      this.$refs.renameModal.hide();
      this.isPostToExchangeModalVisible = false;
      this.postToExchangeLoading = true;
      try {
        const geographicQuestion = this.jobQuestions.find(
          ({ type }) => type === JOB_QUESTION_TYPES.GEOGRAPHY
        );
        const { type, value } =
          getTypeAndValueFromGeographicQuestion(geographicQuestion);
        const data = {
          ...this.profile,
          jobQuestions: this.jobQuestions.filter(
            ({ type }) => type !== JOB_QUESTION_TYPES.GEOGRAPHY
          ),
          geographic_preference_type: type,
          geographic_preference_value: value ? JSON.stringify(value) : null,
        };
        const profile = await Api.jobProfilePostToExchange(data);
        this.$store.commit(UPDATE_JOB_PROFILE_MUTATION, profile);
        this.$toasted.success('Job profile successfully posted', {
          duration: 3000,
        });
        this.resetValidation();
        this.$router.push('/exchange');
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted);
      } finally {
        this.postToExchangeLoading = false;
      }
    },
    onCancelNewJobProfile: function () {
      this.isPostToExchangeProcess = false;
      this.$refs.renameModal.hide();
      // if (this.hasJobProfileTheSavedPreferences || this.isJobProfileNew) {
      //   Help.confirm.show(
      //     'You will lose the ICO if you cancel now. Would you like to continue?',
      //     'Accept',
      //     () => {
      //       this.removeJobProfile();
      //     }
      //   );
      // } else {
      //   this.removeJobProfile();
      // }
    },
    getAnsweredJobQuestions: function () {
      this.answeredJobQuestions = cloneDeep(
        this.allJobQuestions.filter((q) => {
          return isJobQuestionAnswered(q);
        })
      );
    },
  },
  // watch: {
  //   allJobQuestions: function () {
  //     if (this.editedJobQuestionId) return;
  //     this.editedJobQuestionId = this.requiredQuestions[0]
  //       ? this.requiredQuestions[0].id
  //       : this.optionalQuestions[0]
  //       ? this.optionalQuestions[0].id
  //       : null;
  //   },
  // },
  beforeRouteLeave: function (to, from, next) {
    if (this.isSavingInProcess) {
      return next(false);
    }

    return this.isDirty()
      ? Help.confirm.show(
          modifiedConfirmMessage,
          'Leave',
          () => {
            next();
          },
          () => {
            next(false);
          }
        )
      : next();
  },
};
</script>

<style lang="scss">
.job-profile-details {
  padding-top: 55px;
}
</style>
